import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  DollarSign, 
  Calendar, 
  User, 
  Home,
  Building,
  Ruler,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ClipboardList,
  Layers,
  Dna,
  Bed,
  Bath,
  Car,
  TreePine,
  Zap,
  Edit,
  Grid3X3,
  PaintBucket,
  Thermometer,
  Key,
  Fence,
  ChevronRight
} from 'lucide-react';
import InspectionSheetDialog from '../components/InspectionSheetDialog';
import AmenitiesDialog from '../components/AmenitiesDialog';
import ComplianceReportDialog from '../components/ComplianceReportDialog';
import FixCardInspectionDialog from '../components/FixCardInspectionDialog';
import EnvelopeEngineDialog from '../components/EnvelopeEngineDialog';
import PropertyDetailsDialog from '../components/PropertyDetailsDialog';
import ComparableScheduleTable from '../components/ComparableScheduleTable';
import AccommodationSchedule from '../components/accommodation/AccommodationSchedule';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InspectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInspectionSheet, setShowInspectionSheet] = useState(false);
  const [showFixCardSystem, setShowFixCardSystem] = useState(false);
  const [showEnvelopeEngine, setShowEnvelopeEngine] = useState(false);
  const [showAmenities, setShowAmenities] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [showPropertyDetails, setShowPropertyDetails] = useState(false);

  useEffect(() => {
    if (id) {
      fetchInspection();
    }
  }, [id]);

  const fetchInspection = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/inspections/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspection(response.data);
    } catch (err) {
      console.error('Failed to load inspection:', err);
      if (err.response?.status === 404) {
        setError('Inspection not found');
      } else {
        setError('Failed to load inspection details');
      }
      toast.error('Failed to load inspection');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-accent text-white';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-secondary';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="inspection-detail-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading inspection...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="inspection-detail-error">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/inspections')}
            className="mb-4"
            data-testid="back-to-inspections"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Inspections
          </Button>
          
          <Card className="border-destructive">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold text-destructive mb-2">{error}</h2>
              <p className="text-muted-foreground mb-4">
                The inspection you're looking for could not be loaded.
              </p>
              <Button onClick={() => navigate('/inspections')}>
                Return to Inspections
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!inspection) {
    return null;
  }

  const propertyDetails = inspection.property_details || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="inspection-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/inspections')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-primary" data-testid="inspection-address">
                  {inspection.property_address}
                </h1>
                <Badge className={getStatusColor(inspection.status)} data-testid="inspection-status">
                  {inspection.status?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1" data-testid="inspection-id">
                ID: {inspection.id}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPropertyDetails(true)} data-testid="edit-inspection-btn">
              <Edit className="h-4 w-4 mr-2" />
              Edit Property Details
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Valuation Card */}
            <Card data-testid="valuation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-success" />
                  Valuation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-success" data-testid="valuation-amount">
                  {formatCurrency(inspection.valuation_amount)}
                </div>
                {inspection.valuation_amount && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Current market valuation
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Property Details Card */}
            <Card data-testid="property-details-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="h-5 w-5" />
                    Property Details
                  </CardTitle>
                  <CardDescription>
                    Detailed information about the property
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowPropertyDetails(true)}
                  data-testid="edit-property-details-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </CardHeader>
              <CardContent>
                {Object.keys(propertyDetails).length === 0 ? (
                  <div className="text-center py-8">
                    <Home className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground mb-4">
                      No property details have been added yet
                    </p>
                    <Button onClick={() => setShowPropertyDetails(true)}>
                      Add Property Details
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Address Section */}
                    {propertyDetails.address && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Address
                        </h4>
                        <div className="bg-muted/30 rounded-lg p-4">
                          <p className="whitespace-pre-line">{propertyDetails.address}</p>
                        </div>
                      </div>
                    )}

                    {/* Key Specifications */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Key Specifications
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {propertyDetails.property_type && (
                          <div className="flex items-start gap-2">
                            <Building className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Type</p>
                              <p className="font-medium text-sm">{propertyDetails.property_type?.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.property_interest && (
                          <div className="flex items-start gap-2">
                            <Key className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Tenure</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.property_interest?.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.age && (
                          <div className="flex items-start gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Age/Period</p>
                              <p className="font-medium text-sm">{propertyDetails.age?.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.gross_internal_area && (
                          <div className="flex items-start gap-2">
                            <Ruler className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Floor Area</p>
                              <p className="font-medium text-sm">{propertyDetails.gross_internal_area} m²</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.bedrooms !== undefined && propertyDetails.bedrooms !== null && (
                          <div className="flex items-start gap-2">
                            <Bed className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Bedrooms</p>
                              <p className="font-medium text-sm">{propertyDetails.bedrooms}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.bathrooms !== undefined && propertyDetails.bathrooms !== null && (
                          <div className="flex items-start gap-2">
                            <Bath className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Bathrooms</p>
                              <p className="font-medium text-sm">{propertyDetails.bathrooms}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.number_of_stories && (
                          <div className="flex items-start gap-2">
                            <Layers className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Floors</p>
                              <p className="font-medium text-sm">{propertyDetails.number_of_stories}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.floor_level !== undefined && propertyDetails.floor_level !== null && (
                          <div className="flex items-start gap-2">
                            <Grid3X3 className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Floor Level</p>
                              <p className="font-medium text-sm">{propertyDetails.floor_level === '0' ? 'Ground' : propertyDetails.floor_level}</p>
                            </div>
                          </div>
                        )}
                        
                        {propertyDetails.number_of_units && (
                          <div className="flex items-start gap-2">
                            <Home className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-xs text-muted-foreground">Units</p>
                              <p className="font-medium text-sm">{propertyDetails.number_of_units}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Construction Details */}
                    {(propertyDetails.wall_construction || propertyDetails.roof_type || propertyDetails.window_type) && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          <PaintBucket className="h-4 w-4" />
                          Construction
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {propertyDetails.wall_construction && (
                            <div>
                              <p className="text-xs text-muted-foreground">Wall Construction</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.wall_construction?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {propertyDetails.roof_type && (
                            <div>
                              <p className="text-xs text-muted-foreground">Roof Type</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.roof_type?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {propertyDetails.roof_covering && (
                            <div>
                              <p className="text-xs text-muted-foreground">Roof Covering</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.roof_covering?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {propertyDetails.window_type && (
                            <div>
                              <p className="text-xs text-muted-foreground">Window Type</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.window_type?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {propertyDetails.glazing_type && (
                            <div>
                              <p className="text-xs text-muted-foreground">Glazing</p>
                              <p className="font-medium text-sm capitalize">{propertyDetails.glazing_type?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Features */}
                    {(propertyDetails.has_parking || propertyDetails.has_garden || propertyDetails.has_garage || propertyDetails.has_central_heating) && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3">Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {propertyDetails.has_parking && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Car className="h-3 w-3" />
                              Parking
                            </Badge>
                          )}
                          {propertyDetails.has_garden && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <TreePine className="h-3 w-3" />
                              Garden
                            </Badge>
                          )}
                          {propertyDetails.has_garage && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Car className="h-3 w-3" />
                              Garage
                            </Badge>
                          )}
                          {propertyDetails.has_central_heating && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Thermometer className="h-3 w-3" />
                              Central Heating
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Accommodation Summary */}
                    {propertyDetails.accommodation && propertyDetails.accommodation.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          <Layers className="h-4 w-4" />
                          Accommodation ({propertyDetails.accommodation.length} rooms)
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {propertyDetails.accommodation.slice(0, 8).map((room, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {room.type} {room.area_sqm ? `(${room.area_sqm}m²)` : ''}
                            </Badge>
                          ))}
                          {propertyDetails.accommodation.length > 8 && (
                            <Badge variant="outline" className="text-xs">
                              +{propertyDetails.accommodation.length - 8} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Leasehold Details */}
                    {(propertyDetails.property_interest === 'leasehold' || propertyDetails.property_interest === 'share_of_freehold') && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          <Key className="h-4 w-4" />
                          Leasehold Details
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          {propertyDetails.lease_length_years && (
                            <div>
                              <p className="text-xs text-muted-foreground">Lease Length</p>
                              <p className="font-medium text-sm">{propertyDetails.lease_length_years} years</p>
                            </div>
                          )}
                          {propertyDetails.ground_rent_annual && (
                            <div>
                              <p className="text-xs text-muted-foreground">Ground Rent</p>
                              <p className="font-medium text-sm">£{propertyDetails.ground_rent_annual}/year</p>
                            </div>
                          )}
                          {propertyDetails.service_charge_annual && (
                            <div>
                              <p className="text-xs text-muted-foreground">Service Charge</p>
                              <p className="font-medium text-sm">£{propertyDetails.service_charge_annual}/year</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Additional Notes */}
                    {propertyDetails.additional_notes && (
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground mb-2">Notes</h4>
                        <p className="text-sm bg-muted/30 rounded-lg p-3">{propertyDetails.additional_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comparable Evidence Schedule */}
            <ComparableScheduleTable inspection={inspection} />

            {/* Accommodation Schedule */}
            <Card data-testid="accommodation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ruler className="h-5 w-5" />
                  Accommodation Schedule
                </CardTitle>
                <CardDescription>Professional measurement framework — Residential / Commercial</CardDescription>
              </CardHeader>
              <CardContent>
                <AccommodationSchedule inspectionId={id} />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Metadata */}
          <div className="space-y-6">
            {/* Timeline Card */}
            <Card data-testid="timeline-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-success mt-2" />
                  <div>
                    <p className="text-sm font-medium">Created</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(inspection.created_at)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <div>
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(inspection.updated_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assignment Card */}
            <Card data-testid="assignment-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {inspection.assigned_to && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Assigned To</p>
                    <p className="font-medium text-sm">{inspection.assigned_to}</p>
                  </div>
                )}
                
                {inspection.created_by && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Created By</p>
                    <p className="font-medium text-sm">{inspection.created_by}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card data-testid="quick-actions-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Inspection Actions */}
                <div 
                  className="p-3 rounded-lg border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 cursor-pointer transition-all group"
                  onClick={() => setShowInspectionSheet(true)}
                  data-testid="start-inspection-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      <ClipboardList className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Start Inspection Sheet</p>
                      <p className="text-xs text-muted-foreground">Standard property survey form</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  </div>
                </div>

                <div 
                  className="p-3 rounded-lg border-2 border-blue-500/30 hover:border-blue-500 hover:bg-blue-500/5 cursor-pointer transition-all group"
                  onClick={() => setShowFixCardSystem(true)}
                  data-testid="fix-card-inspection-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                      <Layers className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Fix Card Inspection</p>
                      <p className="text-xs text-muted-foreground">Two-layer asset & findings system</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-500" />
                  </div>
                </div>

                <div 
                  className="p-3 rounded-lg border-2 border-purple-500/30 hover:border-purple-500 hover:bg-purple-500/5 cursor-pointer transition-all group"
                  onClick={() => setShowEnvelopeEngine(true)}
                  data-testid="envelope-engine-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500 group-hover:bg-purple-500 group-hover:text-white transition-colors">
                      <Dna className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Envelope Engine</p>
                      <p className="text-xs text-muted-foreground">DNA-driven walkaround inspection</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700">NEW</Badge>
                  </div>
                </div>

                <Separator className="my-3" />

                {/* Supporting Actions */}
                <div 
                  className="p-3 rounded-lg border hover:border-green-500 hover:bg-green-500/5 cursor-pointer transition-all group"
                  onClick={() => setShowAmenities(true)}
                  data-testid="view-amenities-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10 text-green-500 group-hover:bg-green-500 group-hover:text-white transition-colors">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">View Local Amenities</p>
                      <p className="text-xs text-muted-foreground">Nearby stations, schools, shops</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-green-500" />
                  </div>
                </div>

                <div 
                  className="p-3 rounded-lg border hover:border-orange-500 hover:bg-orange-500/5 cursor-pointer transition-all group"
                  onClick={() => setShowReportGenerator(true)}
                  data-testid="generate-report-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/10 text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Generate Report</p>
                      <p className="text-xs text-muted-foreground">RICS Red Book & CPR 35 compliant</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Dialogs */}
        <InspectionSheetDialog
          open={showInspectionSheet}
          onClose={() => setShowInspectionSheet(false)}
          inspection={inspection}
          onSuccess={() => {
            toast.success('Inspection sheet saved');
            fetchInspection();
          }}
        />

        <FixCardInspectionDialog
          open={showFixCardSystem}
          onClose={() => setShowFixCardSystem(false)}
          inspection={inspection}
          onSuccess={(data) => {
            console.log('Fix Card Data:', data);
            toast.success(`Saved ${data.findings.length} findings`);
            fetchInspection();
          }}
        />

        <EnvelopeEngineDialog
          open={showEnvelopeEngine}
          onClose={() => setShowEnvelopeEngine(false)}
          inspection={inspection}
          onSuccess={(data) => {
            console.log('Envelope Engine Data:', data);
            toast.success(`Envelope inspection complete: ${data.fix_cards.length} findings`);
            fetchInspection();
          }}
        />

        <AmenitiesDialog
          open={showAmenities}
          onClose={() => setShowAmenities(false)}
          inspection={inspection}
        />

        <ComplianceReportDialog
          open={showReportGenerator}
          onClose={() => setShowReportGenerator(false)}
          inspection={inspection}
          onSuccess={() => {
            toast.success('Report generated');
          }}
        />

        <PropertyDetailsDialog
          open={showPropertyDetails}
          onClose={() => setShowPropertyDetails(false)}
          inspection={inspection}
          onSuccess={() => {
            fetchInspection();
          }}
        />
      </div>
    </Layout>
  );
}
